import { useState, useEffect, useCallback, useRef } from 'react';
import { CalendarEvent } from '../useSupabaseEvents';
import { getCurrentTimeRounded, getDefaultEndTime } from '../../components/settings/TimePicker';
import { RecurrenceRule } from '../../components/calendar/dialogs/RecurrenceSelector';
import { getDateKey, dateRangeToSelectedCells } from '../../utils/calendar/dateHelpers';
import { resolveCalendar } from '../../utils/calendar/eventHelpers';
import { Calendar } from '../../types';

interface EventFormPosition {
  x: number;
  y: number;
  barRect?: DOMRect;
  centerHorizontally?: boolean;
}

export interface CalendarState {
  // View state
  viewMode: 'year' | 'month' | 'week' | 'schedule' | 'todo';
  currentYear: number;
  currentMonth: number;
  currentWeekStart: Date;
  sidebarCollapsed: boolean;

  // Selection state
  selectedCells: string[];
  isSelecting: boolean;

  // Event form state
  showEventForm: boolean;
  eventFormPosition: EventFormPosition | null;
  eventTitle: string;
  eventNotes: string;
  startDate: string;
  endDate: string;
  selectedCalendar: Calendar | null;
  editingEvent: CalendarEvent | null;
  previewColor: string | null;
  allDay: boolean;
  fullSize: boolean;
  startTime: string;
  handleStartTimeChange: (time: string) => void;
  endTime: string;
  recurrenceRule: RecurrenceRule;

  // Hover state
  hoveredEvent: CalendarEvent | null;
  hoveredEventId: string | number | null;
  hoverCell: string | null;
  tooltipPosition: { x: number; y: number };

  // Filter state
  selectedCalendarFilters: Set<string>;
  searchQuery: string;

  // UI state
  showEventsList: boolean;
  showTagManager: boolean;
  showSettingsMenu: boolean;
  showSettingsPage: boolean;
  showPastDaysGreyed: boolean;
  expandedMonths: Set<number>;
  maxEventRows: number;
  yearZoom: boolean;

  // Delete dialog
  showDeleteDialog: boolean;
  eventToDelete: CalendarEvent | null;
}

interface UseCalendarStateProps {
  calendars: Calendar[];
  calendarsSpaceId: string | null | undefined;
  isTouchDevice: boolean;
  spaceId: string | null | undefined;
}

export const useCalendarState = ({ calendars, calendarsSpaceId, isTouchDevice, spaceId }: UseCalendarStateProps) => {
  // View state — persisted to localStorage so refresh stays on the same page
  const [viewMode, setViewModeRaw] = useState<'year' | 'month' | 'week' | 'schedule' | 'todo'>(() => {
    // First check if user was on a specific view (session persistence)
    const lastUsed = localStorage.getItem('calendar-view-mode');
    if (lastUsed && ['year', 'month', 'week', 'schedule', 'todo'].includes(lastUsed)) {
      return lastUsed as any;
    }
    // Fall back to configured default view
    const defaultView = localStorage.getItem('calendar-default-view');
    if (defaultView && ['year', 'month', 'week', 'schedule', 'todo'].includes(defaultView)) {
      return defaultView as any;
    }
    // Default to month view on mobile (year view is too compressed)
    return isTouchDevice ? 'month' : 'year';
  });
  const setViewMode = (mode: 'year' | 'month' | 'week' | 'schedule' | 'todo') => {
    setViewModeRaw(mode);
    localStorage.setItem('calendar-view-mode', mode);
  };
  const [currentYear, setCurrentYearRaw] = useState(() => {
    const saved = localStorage.getItem('calendar-current-year');
    return saved ? parseInt(saved, 10) : new Date().getFullYear();
  });
  const setCurrentYear = (year: number | ((prev: number) => number)) => {
    setCurrentYearRaw(prev => {
      const next = typeof year === 'function' ? year(prev) : year;
      localStorage.setItem('calendar-current-year', String(next));
      return next;
    });
  };
  const [currentMonth, setCurrentMonthRaw] = useState(() => {
    const saved = localStorage.getItem('calendar-current-month');
    return saved !== null ? parseInt(saved, 10) : new Date().getMonth();
  });
  const setCurrentMonth = (month: number | ((prev: number) => number)) => {
    setCurrentMonthRaw(prev => {
      const next = typeof month === 'function' ? month(prev) : month;
      localStorage.setItem('calendar-current-month', String(next));
      return next;
    });
  };
  const [currentWeekStart, setCurrentWeekStartRaw] = useState(() => {
    const saved = localStorage.getItem('calendar-current-week');
    if (saved) {
      const d = new Date(saved);
      if (!isNaN(d.getTime())) { d.setHours(0, 0, 0, 0); return d; }
    }
    const now = new Date();
    const day = now.getDay();
    const diff = now.getDate() - day + (day === 0 ? -6 : 1);
    const monday = new Date(now.setDate(diff));
    monday.setHours(0, 0, 0, 0);
    return monday;
  });
  const setCurrentWeekStart = (date: Date | ((prev: Date) => Date)) => {
    setCurrentWeekStartRaw(prev => {
      const next = typeof date === 'function' ? date(prev) : date;
      localStorage.setItem('calendar-current-week', next.toISOString());
      return next;
    });
  };
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    const saved = localStorage.getItem('calendar-sidebar-collapsed');
    if (saved !== null) return saved === 'true';
    return isTouchDevice; // default: collapsed on touch, open on desktop
  });

  // Selection state
  const [selectedCells, setSelectedCells] = useState<string[]>([]);
  const [isSelecting, setIsSelecting] = useState(false);

  // Event form state
  const [showEventForm, setShowEventForm] = useState(false);
  const [eventFormPosition, setEventFormPosition] = useState<EventFormPosition | null>(null);
  const [eventTitle, setEventTitle] = useState('');
  const [eventNotes, setEventNotes] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedCalendar, setSelectedCalendar] = useState<Calendar | null>(null);
  const [editingEvent, setEditingEvent] = useState<CalendarEvent | null>(null);
  const [previewColor, setPreviewColor] = useState<string | null>(null);
  const getDefaultDuration = () => localStorage.getItem('calendar-default-event-duration') || 'allday';
  const getDefaultTimes = () => {
    const dur = getDefaultDuration();
    const start = getCurrentTimeRounded();
    if (dur === 'allday') return { allDay: true, start, end: getDefaultEndTime(start) };
    const [sh, sm] = start.split(':').map(Number);
    const offsetMin = parseInt(dur) || 60;
    const endTotal = sh * 60 + sm + offsetMin;
    const eh = Math.floor(endTotal / 60) % 24;
    const em = endTotal % 60;
    return { allDay: false, start, end: `${String(eh).padStart(2, '0')}:${String(em).padStart(2, '0')}` };
  };
  const initTimes = getDefaultTimes();
  const [allDay, setAllDay] = useState(initTimes.allDay);
  const [startTime, setStartTime] = useState(initTimes.start);
  const [endTime, setEndTime] = useState(initTimes.end);
  const [recurrenceRule, setRecurrenceRule] = useState<RecurrenceRule>({ frequency: 'none', interval: 1 });
  const [fullSize, setFullSize] = useState(false);

  // Hover state
  const [hoveredEvent, setHoveredEvent] = useState<CalendarEvent | null>(null);
  const [hoveredEventId, setHoveredEventId] = useState<string | number | null>(null);
  const [hoverCell, setHoverCell] = useState<string | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const tooltipTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Filter state — persist hidden tags to localStorage
  const TAG_FILTER_KEY = 'mozayc-hidden-tags';
  const filtersInitialized = useRef(false);
  const [selectedCalendarFilters, setSelectedCalendarFiltersRaw] = useState<Set<string>>(() => {
    try {
      const stored = localStorage.getItem(TAG_FILTER_KEY);
      if (stored) {
        const hidden = new Set<string>(JSON.parse(stored));
        // Will be reconciled with actual tags in the effect below
        return hidden; // temporarily stores hidden IDs; effect converts to selected
      }
    } catch { /* ignore */ }
    return new Set();
  });
  const setSelectedCalendarFilters = useCallback((filters: Set<string>) => {
    setSelectedCalendarFiltersRaw(filters);
    filtersInitialized.current = true;
  }, []);
  const [searchQuery, setSearchQuery] = useState('');

  // UI state
  const [showEventsList, setShowEventsList] = useState(false);
  const [showTagManager, setShowTagManager] = useState(false);
  const [showSettingsMenu, setShowSettingsMenu] = useState(false);
  const [showSettingsPage, setShowSettingsPage] = useState(() => {
    if (localStorage.getItem('mozayc-return-to') === 'settings-gcal') {
      localStorage.removeItem('mozayc-return-to');
      return true;
    }
    return false;
  });
  const [showPastDaysGreyed, setShowPastDaysGreyed] = useState(false);
  const [expandedMonths, setExpandedMonths] = useState<Set<number>>(new Set());
  const [maxEventRows, setMaxEventRows] = useState<number>(() => {
    const saved = localStorage.getItem('calendar-max-event-rows');
    return saved ? parseInt(saved, 10) : 3;
  });
  const [yearZoom, setYearZoom] = useState(false);

  // Delete dialog
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [eventToDelete, setEventToDelete] = useState<CalendarEvent | null>(null);

  // Track previous space ID to detect navigation changes
  const prevSpaceIdRef = useRef<string | null | undefined>(spaceId);
  // Tracks which spaceId the filters were last initialized for
  const lastFilterInitSpaceIdRef = useRef<string | null | undefined>(spaceId);

  // Reset filters and select all tags when space changes or tags load for the new space
  useEffect(() => {
    const spaceChanged = spaceId !== prevSpaceIdRef.current;
    // Only reset filters when tags are confirmed to belong to the current space.
    // useCalendars keeps stale calendars during fetch to avoid flicker, so calendarsSpaceId
    // lags behind spaceId until the new space's tags actually arrive.
    const tagsAreReady = calendarsSpaceId === spaceId && calendars.length > 0;
    const filterNeedsReset = spaceId !== lastFilterInitSpaceIdRef.current;

    if (spaceChanged) {
      setSearchQuery('');
    }

    if (tagsAreReady) {
      prevSpaceIdRef.current = spaceId;
      const tagExists = selectedCalendar && calendars.some(t => t.id === selectedCalendar.id);
      if (!tagExists || filterNeedsReset) {
        setSelectedCalendar(calendars[0]);
      }
      if (filterNeedsReset || !filtersInitialized.current) {
        const allTagIds = new Set(calendars.map(t => t.id));
        try {
          const stored = localStorage.getItem(TAG_FILTER_KEY);
          if (stored && !filterNeedsReset) {
            const hiddenIds: string[] = JSON.parse(stored);
            const selected = new Set(calendars.map(t => t.id).filter(id => !hiddenIds.includes(id)));
            setSelectedCalendarFiltersRaw(selected);
          } else {
            setSelectedCalendarFiltersRaw(allTagIds);
            localStorage.removeItem(TAG_FILTER_KEY);
          }
        } catch {
          setSelectedCalendarFiltersRaw(allTagIds);
        }
        filtersInitialized.current = true;
        lastFilterInitSpaceIdRef.current = spaceId;
      }
    } else if (!tagsAreReady && calendars.length === 0) {
      prevSpaceIdRef.current = spaceId;
      setSelectedCalendar(null);
    }
  }, [calendars, spaceId, calendarsSpaceId]);

  // Persist tag filter state — store hidden tag IDs
  useEffect(() => {
    if (!filtersInitialized.current || calendars.length === 0) return;
    const allSelected = calendars.every(t => selectedCalendarFilters.has(t.id));
    if (allSelected) {
      localStorage.removeItem(TAG_FILTER_KEY);
    } else {
      const hiddenIds = calendars.filter(t => !selectedCalendarFilters.has(t.id)).map(t => t.id);
      localStorage.setItem(TAG_FILTER_KEY, JSON.stringify(hiddenIds));
    }
  }, [selectedCalendarFilters, calendars]);

  // Persist sidebar state across sessions
  useEffect(() => {
    localStorage.setItem('calendar-sidebar-collapsed', String(sidebarCollapsed));
  }, [sidebarCollapsed]);

  // Reset form helper
  const resetForm = useCallback(() => {
    setSelectedCells([]);
    setIsSelecting(false);
    setShowEventForm(false);
    setEventTitle('');
    setEventNotes('');
    setSelectedCalendar(calendars[0] || null);
    setStartDate('');
    setEndDate('');
    setEditingEvent(null);
    setPreviewColor(null);
    setRecurrenceRule({ frequency: 'none', interval: 1 });
    setFullSize(false);
    const defaults = getDefaultTimes();
    setAllDay(defaults.allDay);
    setStartTime(defaults.start);
    setEndTime(defaults.end);
    setHoverCell(null);
    setEventFormPosition(null);

    // Clear creation preview in CalendarGrid
    if ((window as any).__clearCreation) {
      (window as any).__clearCreation();
    }
  }, [calendars]);

  // Handle cell click for selection
  const handleCellClick = useCallback((year: number, month: number, day: number) => {
    const dateKey = getDateKey(year, month, day);

    if (!isSelecting && !showEventForm) {
      setSelectedCells([dateKey]);
      setIsSelecting(true);
      setStartDate(dateKey);
      setEndDate(dateKey);
    } else if (isSelecting) {
      const startDateStr = selectedCells[0];

      if (startDateStr === dateKey) {
        setSelectedCells([dateKey]);
        setStartDate(dateKey);
        setEndDate(dateKey);
      } else {
        const start = startDateStr <= dateKey ? startDateStr : dateKey;
        const end = startDateStr <= dateKey ? dateKey : startDateStr;

        const range = dateRangeToSelectedCells(start, end);
        setSelectedCells(range);
        setStartDate(start);
        setEndDate(end);
      }

      setShowEventForm(true);
      setIsSelecting(false);
    }
  }, [isSelecting, showEventForm, selectedCells]);

  // Handle creating event from range
  const handleCreateEventRange = useCallback((start: string, end: string, cellRect?: DOMRect) => {
    const range = dateRangeToSelectedCells(start, end);
    setSelectedCells(range);
    setStartDate(start);
    setEndDate(end);

    if (cellRect) {
      setEventFormPosition({
        x: cellRect.left + cellRect.width / 2,
        y: cellRect.top + cellRect.height / 2,
        barRect: cellRect
      });
    } else {
      setEventFormPosition(null);
    }

    setShowEventForm(true);
    setIsSelecting(false);
  }, []);

  // Open a blank event form for a given date (used by header +, week cell click, week drag, year/month cell tap)
  const openNewEventForm = useCallback((config: {
    dateKey: string;
    endDateKey?: string;
    position?: { x: number; y: number; barRect?: DOMRect; centerHorizontally?: boolean };
    allDay?: boolean;
    startTime?: string;
    endTime?: string;
    title?: string;
  }) => {
    const finalEndDateKey = config.endDateKey || config.dateKey;
    const cells = config.endDateKey && config.endDateKey !== config.dateKey
      ? dateRangeToSelectedCells(config.dateKey, finalEndDateKey)
      : [config.dateKey];
    setSelectedCells(cells);
    setStartDate(config.dateKey);
    setEndDate(finalEndDateKey);
    setEventTitle(config.title || '');
    setEventNotes('');
    setEditingEvent(null);

    if (config.allDay !== undefined) setAllDay(config.allDay);
    if (config.startTime !== undefined) setStartTime(config.startTime);
    if (config.endTime !== undefined) setEndTime(config.endTime);

    if (config.position) {
      setEventFormPosition(config.position);
    }

    setShowEventForm(true);
    setIsSelecting(false);
  }, []);

  // Open event for editing
  const openEventForEdit = useCallback((event: CalendarEvent, clickPosition?: { x: number; y: number; barRect?: DOMRect }) => {
    setEditingEvent(event);
    setEventTitle(event.title);
    setEventNotes(event.notes || '');

    if (event.recurrence_rule) {
      setRecurrenceRule(event.recurrence_rule);
    } else {
      setRecurrenceRule({ frequency: 'none', interval: 1 });
    }

    setAllDay(event.all_day !== false);
    setFullSize(event.full_size ?? false);
    const fallbackStart = getCurrentTimeRounded();
    setStartTime(event.start_time || fallbackStart);
    setEndTime(event.end_time || getDefaultEndTime(fallbackStart));

    const matchingTag = resolveCalendar(event, calendars);
    if (matchingTag) {
      setSelectedCalendar(matchingTag);
      setPreviewColor(matchingTag.color);
    } else {
      // Unassigned event - set tag to null
      setSelectedCalendar(null);
      setPreviewColor('bg-unassigned');
    }
    setSelectedCells(event.dates);

    if (event.dates.length > 0) {
      setStartDate(event.dates[0]);
      setEndDate(event.dates[event.dates.length - 1]);
    }

    if (clickPosition) {
      setEventFormPosition(clickPosition);
    } else {
      setEventFormPosition(null);
    }

    setShowEventForm(true);
  }, [calendars]);

  // Toggle month expansion
  const toggleMonthExpansion = useCallback((monthIndex: number) => {
    const newExpanded = new Set(expandedMonths);
    if (newExpanded.has(monthIndex)) {
      newExpanded.delete(monthIndex);
    } else {
      newExpanded.add(monthIndex);
    }
    setExpandedMonths(newExpanded);
  }, [expandedMonths]);

  // Toggle tag filter
  const toggleCalendarFilter = useCallback((tagId: string) => {
    setSelectedCalendarFiltersRaw(prev => {
      const next = new Set(prev);
      if (next.has(tagId)) next.delete(tagId);
      else next.add(tagId);
      return next;
    });
    filtersInitialized.current = true;
  }, []);

  // Adjust end time to maintain duration when start time changes
  const handleStartTimeChange = useCallback((newStart: string) => {
    setStartTime(newStart);
    const [sh, sm] = startTime.split(':').map(Number);
    const [eh, em] = endTime.split(':').map(Number);
    const durationMins = (eh * 60 + em) - (sh * 60 + sm);
    const [nh, nm] = newStart.split(':').map(Number);
    const newEndMins = nh * 60 + nm + (durationMins > 0 ? durationMins : 60);
    if (newEndMins <= 23 * 60 + 59) {
      const h = Math.floor(newEndMins / 60);
      const m = newEndMins % 60;
      setEndTime(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
    } else {
      // Would overflow midnight — clamp to start + 1hr
      const h = (nh + 1) % 24;
      setEndTime(`${String(h).padStart(2, '0')}:${String(nm).padStart(2, '0')}`);
    }
  }, [startTime, endTime]);

  // Event hover handling
  const handleEventHover = useCallback((event: CalendarEvent | null, e: React.MouseEvent | null) => {
    if (tooltipTimerRef.current) {
      clearTimeout(tooltipTimerRef.current);
      tooltipTimerRef.current = null;
    }

    if (event && e) {
      setHoveredEventId(event.id);
      const rect = (e.target as HTMLElement).getBoundingClientRect();
      const newPos = { x: rect.left + rect.width / 2, y: rect.top - 10 };
      setTooltipPosition(newPos);

      // If a tooltip is already visible, update content immediately (no delay)
      setHoveredEvent(prev => {
        if (prev !== null) return event;
        return prev;
      });

      tooltipTimerRef.current = setTimeout(() => {
        setHoveredEvent(event);
      }, 500);
    } else {
      tooltipTimerRef.current = setTimeout(() => {
        setHoveredEventId(null);
        setHoveredEvent(null);
      }, 100);
    }
  }, []);

  // Clear tooltip when the view changes — event bars unmount without firing onMouseLeave
  useEffect(() => {
    if (tooltipTimerRef.current) {
      clearTimeout(tooltipTimerRef.current);
      tooltipTimerRef.current = null;
    }
    setHoveredEvent(null);
    setHoveredEventId(null);
  }, [viewMode]);

  // Prepare for delete
  const prepareDeleteEvent = useCallback((event: CalendarEvent) => {
    if (event.recurrence_rule || event.parent_event_id) {
      setEventToDelete(event);
      setShowDeleteDialog(true);
      setShowEventForm(false);
    } else {
      setEventToDelete(event);
    }
  }, []);

  // Navigation direction for slide transitions
  const [navDirection, setNavDirection] = useState<'prev' | 'next' | null>(null);

  // Counter to trigger scroll-to-today in ScheduleView
  const [scrollToTodayCounter, setScrollToTodayCounter] = useState(0);

  // Navigation handlers — used by CalendarHeader via onPrev/onNext/onToday
  const handleNavPrev = useCallback(() => {
    if (viewMode === 'todo') return;
    setNavDirection('prev');

    if (viewMode === 'year' || viewMode === 'schedule') {
      setCurrentYear(currentYear - 1);
    } else if (viewMode === 'month') {
      if (currentMonth === 0) {
        setCurrentMonth(11);
        setCurrentYear(currentYear - 1);
      } else {
        setCurrentMonth(currentMonth - 1);
      }
    } else {
      const newDate = new Date(currentWeekStart);
      newDate.setDate(newDate.getDate() - 7);
      setCurrentWeekStart(newDate);
      setCurrentYear(newDate.getFullYear());
      setCurrentMonth(newDate.getMonth());
    }
  }, [viewMode, currentYear, currentMonth, currentWeekStart]);

  const handleNavNext = useCallback(() => {
    if (viewMode === 'todo') return;
    setNavDirection('next');

    if (viewMode === 'year' || viewMode === 'schedule') {
      setCurrentYear(currentYear + 1);
    } else if (viewMode === 'month') {
      if (currentMonth === 11) {
        setCurrentMonth(0);
        setCurrentYear(currentYear + 1);
      } else {
        setCurrentMonth(currentMonth + 1);
      }
    } else {
      const newDate = new Date(currentWeekStart);
      newDate.setDate(newDate.getDate() + 7);
      setCurrentWeekStart(newDate);
      setCurrentYear(newDate.getFullYear());
      setCurrentMonth(newDate.getMonth());
    }
  }, [viewMode, currentYear, currentMonth, currentWeekStart]);

  const handleNavToday = useCallback(() => {
    setNavDirection(null);

    const today = new Date();
    const day = today.getDay();
    const diff = today.getDate() - day + (day === 0 ? -6 : 1);
    const monday = new Date(today);
    monday.setDate(diff);
    monday.setHours(0, 0, 0, 0);
    setCurrentYear(today.getFullYear());
    setCurrentMonth(today.getMonth());
    setCurrentWeekStart(monday);
    setScrollToTodayCounter(c => c + 1);
  }, []);

  return {
    // View state
    viewMode, setViewMode,
    currentYear, setCurrentYear,
    currentMonth, setCurrentMonth,
    currentWeekStart, setCurrentWeekStart,
    sidebarCollapsed, setSidebarCollapsed,

    // Selection state
    selectedCells, setSelectedCells,
    isSelecting, setIsSelecting,

    // Event form state
    showEventForm, setShowEventForm,
    eventFormPosition, setEventFormPosition,
    eventTitle, setEventTitle,
    eventNotes, setEventNotes,
    startDate, setStartDate,
    endDate, setEndDate,
    selectedCalendar, setSelectedCalendar,
    editingEvent, setEditingEvent,
    previewColor, setPreviewColor,
    allDay, setAllDay,
    fullSize, setFullSize,
    startTime, setStartTime, handleStartTimeChange,
    endTime, setEndTime,
    recurrenceRule, setRecurrenceRule,

    // Hover state
    hoveredEvent, setHoveredEvent,
    hoveredEventId, setHoveredEventId,
    hoverCell, setHoverCell,
    tooltipPosition, setTooltipPosition,

    // Filter state
    selectedCalendarFilters, setSelectedCalendarFilters,
    searchQuery, setSearchQuery,

    // UI state
    showEventsList, setShowEventsList,
    showTagManager, setShowTagManager,
    showSettingsMenu, setShowSettingsMenu,
    showSettingsPage, setShowSettingsPage,
    showPastDaysGreyed, setShowPastDaysGreyed,
    expandedMonths, setExpandedMonths,
    maxEventRows, setMaxEventRows,
    yearZoom, setYearZoom,

    // Delete dialog
    showDeleteDialog, setShowDeleteDialog,
    eventToDelete, setEventToDelete,

    // Navigation
    navDirection, setNavDirection,
    scrollToTodayCounter,
    handleNavPrev,
    handleNavNext,
    handleNavToday,

    // Actions
    resetForm,
    handleCellClick,
    handleCreateEventRange,
    openNewEventForm,
    openEventForEdit,
    toggleMonthExpansion,
    toggleCalendarFilter,
    handleEventHover,
    prepareDeleteEvent,
  };
};
